import PropTypes from "prop-types";

import { useDropzone } from "react-dropzone";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
// material
import { CircularProgress } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import {
  Box,
  List,
  Stack,
  Paper,
  Button,
  ListItem,
  Typography,
  Skeleton,
} from "@mui/material";
// utils
import { fData } from "../../utils/formatNumber";
//
import { MIconButton } from "../@material-extend";
import { varFadeInRight } from "../animate";
import { UploadIllustration } from "../../assets";
import { useTranslation } from "react-i18next";
import { getFileExtension, getImageUrl } from "src/utils/imageUrl";

// ----------------------------------------------------------------------

const DropZoneStyle = styled("div")(({ theme }) => ({
  outline: "none",
  display: "flex",
  textAlign: "center",
  alignItems: "center",
  flexDirection: "column",
  justifyContent: "center",
  padding: theme.spacing(5, 1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.neutral,
  border: `1px dashed ${theme.palette.grey[500_32]}`,
  "&:hover": { opacity: 0.72, cursor: "pointer" },
  [theme.breakpoints.up("md")]: { textAlign: "left", flexDirection: "row" },
}));

// ----------------------------------------------------------------------

UploadMultiFile.propTypes = {
  error: PropTypes.bool,
  showPreview: PropTypes.bool,
  files: PropTypes.array,
  onRemove: PropTypes.func,
  onRemoveAll: PropTypes.func,
  sx: PropTypes.object,
};

export default function UploadMultiFile({
  error,
  showPreview = false,
  files,
  onRemove,
  blob,
  type,
  imageLoading,
  isInitialized,
  isEdit,
  deleteIndex,
  deleteLoading,
  onRemoveAll,
  loading,
  sx,
  ...other
}) {
  const hasFile = files?.length > 0;
  const { t } = useTranslation("common");
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    fileRejections,
  } = useDropzone({
    ...other,
  });
  const ShowRejectionItems = () => (
    <Paper
      variant="outlined"
      sx={{
        py: 1,
        px: 2,
        mt: 3,
        borderColor: "error.light",
        bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
      }}
    >
      {fileRejections.map(({ file, errors }) => {
        const { path, size } = file;
        console.log(error);
        return (
          <Box key={path} sx={{ my: 1 }}>
            <Typography variant="subtitle2" noWrap>
              {path} - {fData(size)}
            </Typography>
            {errors.map((e) => (
              <Typography key={e.code} variant="caption" component="p">
                - {e.message}
              </Typography>
            ))}
          </Box>
        );
      })}
    </Paper>
  );
  return (
    <Box sx={{ width: "100%", ...sx }}>
      <DropZoneStyle
        {...getRootProps()}
        sx={{
          ...(isDragActive && { opacity: 0.72 }),
          ...((isDragReject || error) && {
            color: "error.main",
            borderColor: "error.light",
            bgcolor: "error.lighter",
          }),
        }}
      >
        <input {...getInputProps()} disabled={loading} />
        <Box sx={{ p: 2, ml: { md: 2 } }}>
          <Typography variant={"h5"}>
            {type == "map"
              ? t("drop-or-select-multiple-files")
              : t("drop-or-select-multiple-images")}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {t("drop-image-here-or-click")}&nbsp;
            <Typography
              variant="body2"
              component="span"
              sx={{ color: "primary.main", textDecoration: "underline" }}
            >
              {t("browse")}
            </Typography>
            &nbsp;{t("thorough-your-machine")}
          </Typography>
        </Box>
      </DropZoneStyle>
      {fileRejections.length > 0 && <ShowRejectionItems />}
      <List disablePadding sx={{ ...(hasFile && { my: 3 }) }}>
        {(loading
          ? [...Array(!isEdit ? files.length + blob.length : blob.length)]
          : files
        )?.map((file, index) =>
          imageLoading ||
          loading ||
          (index === deleteIndex && type !== "map") ? (
            <ListItem
              key={Math.random()}
              {...varFadeInRight}
              sx={{
                my: 1,
                p: 0,
                width: 80,
                height: 80,
                borderRadius: 1,
                display: "inline-flex",
                mx: 0.5,
                border: (theme) => `solid 2px ${theme.palette.divider}`,
              }}
            >
              <Skeleton
                variant="rectagular"
                animation={"wave"}
                width={"100%"}
                height={"100%"}
              />
            </ListItem>
          ) : typeof file == "string" ? (
            <ListItem
              key={Math.random()}
              {...varFadeInRight}
              sx={{
                p: 0,
                m: 0.5,
                width: 80,
                height: 80,
                borderRadius: 1.5,
                overflow: "hidden",
                position: "relative",
                display: "inline-flex",
              }}
            >
              <Paper
                variant="outlined"
                component="img"
                src={
                  isEdit
                    ? getFileExtension(file) === "pdf"
                      ? "/icons/pdf.png"
                      : getFileExtension(file) === "dxf"
                      ? "/icons/cad.png"
                      : getImageUrl(file)
                    : file
                }
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  position: "absolute",
                }}
              />

              <Box sx={{ top: 6, right: 6, position: "absolute" }}>
                <MIconButton
                  size="small"
                  onClick={() => onRemove(file, index)}
                  sx={{
                    p: "2px",
                    color: "common.white",
                    bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
                    "&:hover": {
                      bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48),
                    },
                  }}
                >
                  <CloseRoundedIcon />
                </MIconButton>
              </Box>
            </ListItem>
          ) : (
            <ListItem
              key={Math.random()}
              {...varFadeInRight}
              sx={{
                p: 0,
                m: 0.5,
                width: 80,
                height: 80,
                borderRadius: 1.5,
                overflow: "hidden",
                position: "relative",
                display: "inline-flex",
              }}
            >
              <Paper
                variant="outlined"
                component="img"
                src={"/icons/pdf.png"}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  position: "absolute",
                }}
              />

              <Box sx={{ top: 6, right: 6, position: "absolute" }}>
                <MIconButton
                  size="small"
                  onClick={() => onRemove(file, index)}
                  sx={{
                    p: "2px",
                    color: "common.white",
                    bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
                    "&:hover": {
                      bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48),
                    },
                  }}
                >
                  <CloseRoundedIcon />
                </MIconButton>
              </Box>
            </ListItem>
          )
        )}
      </List>

      <Stack direction="row" justifyContent="flex-end">
        {loading ? (
          <Skeleton
            variant="rectagular"
            width={106}
            height={36}
            sx={{ mr: 1.5 }}
          />
        ) : (
          hasFile && (
            <Button variant="contained" onClick={onRemoveAll} sx={{ mr: 1.5 }}>
              {t("remove-all")}
            </Button>
          )
        )}
      </Stack>
    </Box>
  );
}
